// A "Ducks"-style collection
// https://github.com/erikras/ducks-modular-redux

import { get, cloneDeep, isEmpty, omit } from 'lodash';

// constants
import { applicationTypes, scriptMethods, scriptTypes } from 'constants/scripts';
import { APPLICATION_FORMAT } from 'constants/values';

// actions
import { setManualScriptParams } from 'manual';
import { setValidationResult } from 'validations';
import { lockPlantingPlan, unlockPlantingPlan } from 'current/uiwidgets/locks';

// finders
import { findActivitiesForFieldUuid, findActivity } from 'activities/finders';

// selectors
import {
  getActivityByActivityCategoryUuid,
  getDefaultDateForActivityCategoryUuid
} from 'activities/selectors';
import {
  getAccountCountry,
  getProductValidationValues
} from 'user/selectors';
import { getCurrency } from 'user/userSelectors';

import {
  getActiveFieldUuid,
  getActiveScriptMethod,
  getActiveScriptType
} from 'current/selectors';

import {
  getActiveCropId,
} from 'current/currentSelectors';

import {
  getDefaultScriptName,
  getPlantingPlanParams
} from 'planting-plan-form/selectors';

import {
  getProductYearFromSeasonCode
} from 'scripts/selectors';

import {
  getProductYearByScriptType,
  isPastProductYearForScriptType
} from 'settings/selectors';

// util
import {
  formatPlantingPlanParams,
  mergePlantingPlanParams
} from 'planting-plan-form/plantingPlanParamsUtil.js';

// validators
import {
  validateAdvancedPlantingPlan,
  validateAdvancedPlantingPlanUi,
  validateManualPlantingPlan,
  validateManualFertilityPlan,
  validateManualCropProtectionPlan
} from 'current/validators';

// Action Types
export const typePrefix = 'vra/planting-plan-form/';
export const actions = [
  'RESET_REVIEW_SCENARIO_PLANTING_PLAN_PARAMS',
  'RESET_PLANTING_PLAN_PARAMS',
  'SET_PLANTING_PLAN_PARAMS',
  'UPDATE_PLANTING_PLAN_NUM_ZONES',

  // analytics
  'SET_PLANTING_PLAN_TARGET_YIELD',
  'SET_PLANTING_PLAN_POPULATION',
  'SET_PLANTING_PLAN_PRICE_COMMODITY',
  'SET_PLANTING_PLAN_PRICE_PRODUCT',
  'SET_PLANTING_PLAN_GOAL_RATIO',
  'SET_PLANTING_PLAN_DATE',
  'SET_PLANTING_PLAN_INCORPORATION_METHOD',
  'SET_PLANTING_PLAN_NITRAPYRIN',
  'SET_PLANTING_PLAN_PRODUCT',
  'SET_PLANTING_PLAN_SUBSTANCE_TYPE',
  'SET_PLANTING_PLAN_FERTILITY_ACTIVITY',
  'SET_PLANTING_PLAN_APPLICATION_RATE',
  'SET_PLANTING_PLAN_AGRONOMIC_SEASON',
  'UPDATE_PLANTING_PLAN'
].reduce((result, key) => { result[key] = `${typePrefix}${key}`; return result; }, {});

// Reducer

const initialParams = {
  brand: '',
  'goal-ratio': '50',
  hasPlantingFertilizerApplication: false,
  products: {
    [applicationTypes.SEED]: {},
    [applicationTypes.FERTILIZER]: {},
    [applicationTypes.APPLICATIONV2]: []
  }
};

export default function reducer(
  state = {
    params: Object.assign({}, cloneDeep(initialParams))
  },
  action = {}
) {
  switch (action.type) {
    case actions.RESET_PLANTING_PLAN_PARAMS:
      return {
        ...state,
        params: Object.assign({}, cloneDeep(initialParams))
      };

    case actions.RESET_REVIEW_SCENARIO_PLANTING_PLAN_PARAMS:
      return {
        ...state,
        params: omit({ ...state.params, 'goal-ratio': '50' }, ['num-zones'])
      };

    case actions.SET_PLANTING_PLAN_PARAMS:
      return {
        ...state,
        params: action.payload.plantingPlanParams
      };

    case actions.UPDATE_PLANTING_PLAN_NUM_ZONES:
      return {
        ...state,
        params: { ...state.params, 'num-zones': action.payload.numZones }
      };

    default:
      return state;
  }
}

// Action Creators

export const resetReviewScenarioPlantingPlanParams = () => {
  return {
    type: actions.RESET_REVIEW_SCENARIO_PLANTING_PLAN_PARAMS
  };
};

export const resetPlantingPlanParams = () => {
  return {
    type: actions.RESET_PLANTING_PLAN_PARAMS
  };
};

export const setPlantingPlanParams = (plantingPlanParams) => {
  return {
    type: actions.SET_PLANTING_PLAN_PARAMS,
    payload: { plantingPlanParams }
  };
};

export const updatePlantingPlanNumZones = (numZones) => {
  return {
    type: actions.UPDATE_PLANTING_PLAN_NUM_ZONES,
    payload: { numZones }
  };
};

export const validatePlantingPlan = ({
  scriptMethod,
  scriptType,
  plantingPlanParams,
  resultedFromUserInput = true,
}) => {
  return (dispatch, getState) => {
    let rawValidatorResult;
    const productValidationValues = getProductValidationValues(getState());
    switch (scriptType) {
      case scriptTypes.APPLICATION:
        rawValidatorResult = validateManualCropProtectionPlan({ getState, plantingPlanParams });
        break;
      case scriptTypes.FERTILITY:
        rawValidatorResult = validateManualFertilityPlan({ getState, plantingPlanParams });
        dispatch(setValidationResult({ key: 'plantingPlanParams', resultedFromUserInput, rawValidatorResult }));
        break;
      case scriptTypes.PLANTING:
        const currency = getCurrency(getState());
        const country = getAccountCountry(getState());
        const cropId = getActiveCropId(getState());
        if (scriptMethod === scriptMethods.ADVANCED) {
          // 1st validation is overall planting plan, all params present to continue -- errors here will
          // not be displayed to the user
          rawValidatorResult = validateAdvancedPlantingPlan({
            cropId,
            currency,
            plantingPlanParams,
            productValidationValues
          });
          // set a 2nd validation of ui facing errors, only user input errors
          dispatch(setValidationResult({
            key: 'plantingPlanParamsUi',
            resultedFromUserInput,
            rawValidatorResult: validateAdvancedPlantingPlanUi({
              country,
              currency,
              plantingPlanParams,
              productValidationValues,
              cropId
            })
          }));
        } else {
          rawValidatorResult = validateManualPlantingPlan({ getState, plantingPlanParams, currency });
        }
        dispatch(setValidationResult({ key: 'plantingPlanParams', resultedFromUserInput, rawValidatorResult }));
        break;
      default:
    }
  };
};

export const updatePlantingPlanParams = (newParams = {}, clearExistingProducts) => {
  return (dispatch, getState) => { // NOSONAR Action creator
    const {
      activityCategoryUuid: updatedActivityCategoryUuid,
      date: updatedDate,
      substanceTypeUuid: updatedSubstanceType,
    } = newParams;

    const state = getState();
    const scriptType = getActiveScriptType(state);
    let currentParams = getPlantingPlanParams(state);

    if (updatedSubstanceType) {
      currentParams.products[applicationTypes.FERTILIZER] = {};
    }

    if (clearExistingProducts) {
      currentParams.products.APPLICATION_v2 = [];
    }

    let plantingPlanParams = mergePlantingPlanParams({ currentParams, newParams });

    // override existing products if we pass in an empty object
    if (newParams.products && isEmpty(newParams.products[scriptType])) {
      plantingPlanParams = {
        ...plantingPlanParams,
        products: newParams.products
      };
    }

    // set activity type for activitycategoryuuid selected
    if (updatedActivityCategoryUuid) {
      plantingPlanParams.activityType = get(
        state,
        `activities.categories.byUuid[${updatedActivityCategoryUuid}].activityType`
      );
    }
    plantingPlanParams = formatPlantingPlanParams(plantingPlanParams);

    if (
      (!updatedDate || updatedDate === currentParams.date)
      && (!updatedActivityCategoryUuid || updatedActivityCategoryUuid === currentParams.activityCategoryUuid)
    ) {
      // If we're not changing the date, we don't need to call findActivity
      plantingPlanParams = mergePlantingPlanParams({ currentParams, newParams });
      plantingPlanParams.defaultScriptName = getDefaultScriptName(state, plantingPlanParams);

      const scriptMethod = getActiveScriptMethod(state);
      dispatch(validatePlantingPlan({ scriptMethod, scriptType, plantingPlanParams }));
      dispatch(setPlantingPlanParams(plantingPlanParams));
      return null;
    }

    const displayActivityCategoryUuid = updatedActivityCategoryUuid || currentParams.activityCategoryUuid;

    const basisActivity = getActivityByActivityCategoryUuid(state, displayActivityCategoryUuid);

    const fillerUi = {
      ...currentParams,
      ...newParams
      // UI to reflect immediate change while waiting on services
      // fill with the params we currently know so they don't get blanked out in the ui
    };

    const productYear = basisActivity
      ? getProductYearFromSeasonCode(basisActivity.season)
      : getProductYearByScriptType(state, scriptType);
    if (updatedDate || isPastProductYearForScriptType({ productYear, scriptType, state })) {
      const defaultDate = getDefaultDateForActivityCategoryUuid(state, displayActivityCategoryUuid);
      fillerUi.date = updatedDate || defaultDate;
    }
    if (updatedDate || updatedActivityCategoryUuid) {
      dispatch(resetPlantingPlanParams());
      fillerUi.activityCategoryUuid = displayActivityCategoryUuid;
    }

    if (!isEmpty(fillerUi)) {
      dispatch(setPlantingPlanParams(fillerUi));
    }

    dispatch(lockPlantingPlan());
    return findActivity({ dispatch, getState, params: { ...plantingPlanParams, updatedDate } })
    .then((activity) => {
      const activityUuid = get(activity, 'uuid');
      const season = get(activity, 'season');
      const date = get(activity, 'date') || updatedDate;

      plantingPlanParams = { ...plantingPlanParams, activityUuid, date, seasonCode: season };
      // update the default script name with the current params (product, date, etc.)
      plantingPlanParams.defaultScriptName = getDefaultScriptName(state, plantingPlanParams);

      const scriptMethod = getActiveScriptMethod(state);
      dispatch(validatePlantingPlan({ scriptMethod, scriptType, plantingPlanParams }));
      dispatch(setPlantingPlanParams(plantingPlanParams));

      if (scriptMethod === scriptMethods.MANUAL) {
        dispatch(setManualScriptParams({ activityUuid }));
      }

      dispatch(unlockPlantingPlan());
    });
  };
};

export const initializePlantingPlanParams = ({ plantingPlanParams }) => {
  return (dispatch, getState) => {
    const state = getState();
    const scriptType = getActiveScriptType(state);
    const scriptMethod = getActiveScriptMethod(state);
    let currentParams = getPlantingPlanParams(state);
    const plantingPlanParamsToInitialize = mergePlantingPlanParams({ currentParams, newParams: plantingPlanParams });
    const fieldUuid = getActiveFieldUuid(state);

    // initialize with the activity, since for planting, we don't get the option of choosing activity category, so need
    // to have the activity in place when hitting the planting plan screen
    dispatch(lockPlantingPlan());

    return findActivitiesForFieldUuid({ dispatch, getState, fieldUuid })
    .then(() => findActivity({ dispatch, getState, params: plantingPlanParamsToInitialize }))
    .then((activity) => {
      const activityUuid = get(activity, 'uuid');
      const season = get(activity, 'season');
      const date = get(activity, 'date');

      plantingPlanParams = {
        ...plantingPlanParamsToInitialize,
        activityUuid,
        date,
        seasonCode: season,
        ...(scriptType === applicationTypes.APPLICATION && { incorporationMethod: APPLICATION_FORMAT.sprayed })
      };
      // update the default script name with the current params (product, date, etc.)
      plantingPlanParams.defaultScriptName = getDefaultScriptName(state, plantingPlanParams);

      dispatch(validatePlantingPlan({
        scriptMethod,
        scriptType,
        plantingPlanParams,
        resultedFromUserInput: false
      }));
      dispatch(setPlantingPlanParams(plantingPlanParams));
      if (scriptMethod === scriptMethods.MANUAL && scriptType === scriptTypes.PLANTING) {
        dispatch(setManualScriptParams({ activityUuid }));
      }
      dispatch(unlockPlantingPlan());
    });
  };
};

export const updateProductApplicationRate = (applicationRate) => ({
  type: actions.SET_PLANTING_PLAN_APPLICATION_RATE,
  payload: { applicationRate }
});
